import { UserStepAnswer } from 'pages/new-questions/components/DecisionTreeQuestion/utils/types';
import {
  LectureAnswersState,
  ReviewResponse,
  ReviewQuestion,
  LectureData,
  LectureQuestion,
  IDK_ANSWER_ID,
} from '../types';
import { parseTimeToSeconds } from './index';
import { Answer } from 'pages/new-questions/types';

export const processReviewResponse = (
  reviewData: ReviewResponse,
  lecture: LectureData
): {
  answers: LectureAnswersState;
  totalTime: number;
  reviewInfo: {
    examId: number;
    date: string;
    timeSpent: string;
  };
} => {
  const answers: LectureAnswersState = {
    objective: {},
    context: {},
    decisionTree: {},
    caseStudy: {},
    questionTime: {},
    contador: reviewData.exam_info.time_spent,
  };

  const objectiveQuestions = reviewData.questions.filter(
    (q): q is Extract<ReviewQuestion, { question_type: 'objective_questions' }> =>
      q.question_type === 'objective_questions'
  );

  objectiveQuestions.forEach((q) => {
    answers.objective[q.question_id] = q.answer_id;
  });

  const contextQuestions = reviewData.questions.filter(
    (q): q is Extract<ReviewQuestion, { question_type: 'question_context' }> =>
      q.question_type === 'question_context'
  );

  contextQuestions.forEach((q) => {
    let answerText = q.context_answers || '';
    try {
      const parsed = JSON.parse(q.context_answers);
      if (parsed && typeof parsed === 'object' && parsed.answer) {
        answerText = parsed.answer;
      }
    } catch (e) {
      // Keep original text if not JSON
    }
    answers.context[q.question_id] = answerText;
    answers.questionTime[q.question_id] = q.time_spent;
  });

  const decisionTreeQuestions = reviewData.questions.filter(
    (q): q is Extract<ReviewQuestion, { question_type: 'decision_tree' }> =>
      q.question_type === 'decision_tree'
  );

  const decisionTreeMap = new Map<number, typeof decisionTreeQuestions>();
  decisionTreeQuestions.forEach((q) => {
    if (!decisionTreeMap.has(q.question_id)) {
      decisionTreeMap.set(q.question_id, []);
    }
    decisionTreeMap.get(q.question_id)!.push(q);
  });

  decisionTreeMap.forEach((steps, questionId) => {
    const question = lecture.questions.find(
      (q) => q.id_questao === questionId && q.question_type === 'decision_tree'
    ) as Extract<LectureQuestion, { question_type: 'decision_tree' }> | undefined;

    if (!question) return;

    const userAnswers: UserStepAnswer[] = [];

    steps.forEach((step) => {
      let stepIndex = -1;
      let stepType: 'multiple' | 'single' = 'single';
      let questionText = '';
      let allAnswers: Answer[] = [];

      const multipleStepIndex = question.step_multiple?.findIndex(
        (s) => s.id === step.question_step_id
      );
      if (multipleStepIndex !== undefined && multipleStepIndex !== -1) {
        stepIndex = multipleStepIndex;
        stepType = 'multiple';
        const stepData = question.step_multiple![multipleStepIndex];
        questionText = stepData.statement;
        allAnswers = stepData.answers;
      } else {
        const singleStepIndex = question.step_single?.findIndex(
          (s) => s.id === step.question_step_id
        );
        if (singleStepIndex !== undefined && singleStepIndex !== -1) {
          stepIndex = singleStepIndex;
          stepType = 'single';
          const stepData = question.step_single![singleStepIndex];
          questionText = stepData.statement;
          allAnswers = stepData.answers;
        }
      }

      if (stepIndex !== -1) {
        const markedAnswers = allAnswers.map((ans) => ({
          ...ans,
          correct: ans.answer_id === step.correct_answer_id ? 1 : 0,
        }));

        userAnswers.push({
          stepIndex,
          stepType,
          questionText,
          selectedAnswerId: step.answer_id,
          isCorrect: step.is_correct,
          allAnswers: markedAnswers,
        });
      }

      if (!answers.questionTime[questionId]) {
        answers.questionTime[questionId] = step.time_spent;
      }
    });

    answers.decisionTree[questionId] = userAnswers;
  });

  // Processa questões de estudo de caso (mesmo shape de step que decision_tree)
  const caseStudyReviewQuestions = reviewData.questions.filter(
    (q): q is Extract<ReviewQuestion, { question_type: 'case_study' }> =>
      q.question_type === 'case_study'
  );

  const caseStudyMap = new Map<number, typeof caseStudyReviewQuestions>();
  caseStudyReviewQuestions.forEach((q) => {
    if (!caseStudyMap.has(q.question_id)) {
      caseStudyMap.set(q.question_id, []);
    }
    caseStudyMap.get(q.question_id)!.push(q);
  });

  caseStudyMap.forEach((steps, questionId) => {
    const stepAnswers: Record<number, number> = {};

    steps.forEach((step) => {
      stepAnswers[step.question_step_id] = step.answer_id;

      if (!answers.questionTime[questionId]) {
        answers.questionTime[questionId] = step.time_spent;
      }
    });

    answers.caseStudy[questionId] = stepAnswers;
  });

  const totalTime = parseTimeToSeconds(reviewData.exam_info.time_spent);

  return {
    answers,
    totalTime,
    reviewInfo: {
      examId: reviewData.exam_info.id_prova,
      date: reviewData.exam_info.date,
      timeSpent: reviewData.exam_info.time_spent,
    },
  };
};

export const markObjectiveAnswers = (
  lecture: LectureData,
  reviewData: ReviewResponse
): LectureData => {
  const markedLecture = { ...lecture };

  markedLecture.questions = lecture.questions.map((question) => {
    if (question.question_type !== 'objective_questions') {
      return question;
    }

    const reviewQuestion = reviewData.questions.find(
      (q) => q.question_type === 'objective_questions' && q.question_id === question.id_questao
    ) as Extract<ReviewQuestion, { question_type: 'objective_questions' }> | undefined;

    if (!reviewQuestion) {
      return question;
    }

    const userMarkedIdk = reviewQuestion.answer_id === IDK_ANSWER_ID;

    const markedAnswers = question.answers.map((answer) => ({
      ...answer,
      answereds: [
        {
          is_marked: !userMarkedIdk && answer.id_resposta === reviewQuestion.answer_id,
          is_correct: answer.id_resposta === reviewQuestion.correct_answer_id,
        },
      ],
    }));

    return {
      ...question,
      answers: markedAnswers,
      userMarkedIdk,
    };
  });

  return markedLecture;
};

export const markDecisionTreeAnswers = (
  lecture: LectureData,
  reviewData: ReviewResponse
): LectureData => {
  const markedLecture = { ...lecture };

  markedLecture.questions = lecture.questions.map((question) => {
    if (question.question_type !== 'decision_tree') {
      return question;
    }

    const reviewQuestions = reviewData.questions.filter(
      (q) => q.question_type === 'decision_tree' && q.question_id === question.id_questao
    ) as Extract<ReviewQuestion, { question_type: 'decision_tree' }>[];

    if (reviewQuestions.length === 0) {
      return question;
    }

    const markedStepMultiple = question.step_multiple?.map((step) => {
      const reviewStep = reviewQuestions.find((q) => q.question_step_id === step.id);
      if (!reviewStep) return step;

      return {
        ...step,
        answereds: [
          {
            answer_id: reviewStep.answer_id,
            is_correct: reviewStep.is_correct,
            correct_answer_id: reviewStep.correct_answer_id,
          },
        ],
        answers: step.answers.map((answer) => ({
          ...answer,
          answereds: [
            {
              is_marked: answer.answer_id === reviewStep.answer_id,
              is_correct: answer.answer_id === reviewStep.correct_answer_id,
            },
          ],
        })),
      };
    });

    const markedStepSingle = question.step_single?.map((step) => {
      const reviewStep = reviewQuestions.find((q) => q.question_step_id === step.id);
      if (!reviewStep) return step;

      return {
        ...step,
        answereds: [
          {
            answer_id: reviewStep.answer_id,
            is_correct: reviewStep.is_correct,
            correct_answer_id: reviewStep.correct_answer_id,
          },
        ],
        answers: step.answers.map((answer) => ({
          ...answer,
          answereds: [
            {
              is_marked: answer.answer_id === reviewStep.answer_id,
              is_correct: answer.answer_id === reviewStep.correct_answer_id,
            },
          ],
        })),
      };
    });

    return {
      ...question,
      step_multiple: markedStepMultiple,
      step_single: markedStepSingle,
    };
  });

  return markedLecture;
};

export const markContextAnswers = (
  lecture: LectureData,
  reviewData: ReviewResponse
): LectureData => {
  const markedLecture = { ...lecture };

  markedLecture.questions = lecture.questions.map((question) => {
    if (question.question_type !== 'question_context') {
      return question;
    }

    const reviewQuestion = reviewData.questions.find(
      (q) => q.question_type === 'question_context' && q.question_id === question.id_questao
    ) as Extract<ReviewQuestion, { question_type: 'question_context' }> | undefined;

    if (!reviewQuestion) {
      return question;
    }

    let userAnswer = '';
    try {
      const contextData = JSON.parse(reviewQuestion.context_answers);
      userAnswer = contextData.answer || '';
    } catch {
      userAnswer = reviewQuestion.context_answers || '';
    }

    const evaluation = reviewQuestion.evaluation
      ? {
          score: reviewQuestion.evaluation.nota,
          maxScore: 100,
          feedback: reviewQuestion.evaluation.justificativa,
          evaluatedAt: new Date().toISOString(),
          isCorrect: reviewQuestion.is_correct,
          verdict: reviewQuestion.evaluation.veredicto,
          evaluationMethod: 'ai' as const,
        }
      : undefined;

    return {
      ...question,
      answereds: [
        {
          context_answers: reviewQuestion.context_answers,
          answer: userAnswer,
          time_spent: reviewQuestion.time_spent,
        },
      ],
      evaluation,
    };
  });

  return markedLecture;
};

export const markCaseStudyAnswers = (
  lecture: LectureData,
  reviewData: ReviewResponse
): LectureData => {
  const markedLecture = { ...lecture };

  markedLecture.questions = lecture.questions.map((question) => {
    if (question.question_type !== 'case_study') {
      return question;
    }

    const reviewSteps = reviewData.questions.filter(
      (q) => q.question_type === 'case_study' && q.question_id === question.id_questao
    ) as Extract<ReviewQuestion, { question_type: 'case_study' }>[];

    if (reviewSteps.length === 0) return question;

    const markedStepMultiple = question.step_multiple.map((step) => {
      const reviewStep = reviewSteps.find((q) => q.question_step_id === step.id);
      if (!reviewStep) return step;

      return {
        ...step,
        answereds: [
          {
            answer_id: reviewStep.answer_id,
            is_correct: reviewStep.is_correct,
            correct_answer_id: reviewStep.correct_answer_id,
          },
        ],
        answers: step.answers.map((answer) => ({
          ...answer,
          answereds: [
            {
              is_marked: answer.answer_id === reviewStep.answer_id,
              is_correct: answer.answer_id === reviewStep.correct_answer_id,
            },
          ],
        })),
      };
    });

    return { ...question, step_multiple: markedStepMultiple };
  });

  return markedLecture;
};
